import * as React from 'react'
import cx from 'classnames'
import type {
  LabeledHelperTextProps,
  SizeTypes
} from '@toasttab/buffet-pui-text-base'
import { HelperText, Label } from '@toasttab/buffet-pui-text-base'
import { useUniqueId } from '@toasttab/buffet-utils'
import { TextInput } from '../TextInput'
import { Separator } from './Separator'
import { useCodeInput } from './useCodeInput'
import { loadStrings, t } from '../defaultStrings'

export interface CodeInputProps
  extends Omit<LabeledHelperTextProps, 'helperIconButton'> {
  /**
   * The number of digits required for a complete code.
   */
  length: number
  /**
   * Whether the component should handle code received via SMS. Defaults to
   * `false`. Be careful, this may not work with all the devices (or not at
   * all).
   */
  autoComplete?: boolean
  /**
   * Class names to be applied to the whole component. Defaults to nothing.
   */
  containerClassName?: string
  /**
   * A variable that represents the current state of the code. Should be
   * updated when calling `onCodeChange`. Can be tied to a React State, or
   * Formik for example. It is recommend to use the `emptyStringArray` to
   * properly initialize the code. Defaults to nothing.
   * ```jsx
   * const [input, setInput] = React.useState(emptyStringArray(6))
   * return (
   *   <CodeInput
   *     length={6}
   *     code={input}
   *     onCodeChange={(code) => setInput(code)}
   *   />
   * )
   * ```
   */
  code?: string[]
  /**
   * A separator that can be inserted between inputs, based on
   * `shouldShowSeparator`. Can be a simple string, or a React Component.
   * Defaults to nothing.
   */
  separator?: React.ReactNode
  /**
   * Size of individual inputs. It is recommended to not be set in order to
   * use the default value, unless you have a specific use case.
   * Defaults to `auto`.
   */
  size?: SizeTypes
  /**
   * A function called everytime a new character is typed, in order to maintain
   * to the code state. Can be tied to a React State, or Formik for example.
   * While providing the next code, it also passes a `isComplete` boolean to let
   * you know that the typed code reach the length. Defaults to nothing.
   * @example
   * ```jsx
   * const [input, setInput] = React.useState(emptyStringArray(6))
   * const [hasCodeBeenTyped, setHasCodeBeenTyped] = React.useState(false)
   * return (
   *   <CodeInput
   *     length={6}
   *     code={input}
   *     onCodeChange={(code, isComplete) => {
   *       setInput(code)
   *       if (isComplete) {
   *         setHasCodeBeenTyped(true)
   *         // Formats correctly before using it
   *         sendCodeToBackEnd(code.join(''))
   *       }
   *     }}
   *     disabled={hasCodeBeenTyped}
   *   />
   * )
   * ```
   */
  onCodeChange?(input: string[], isComplete: boolean): void
  /**
   * A function that takes the index of the current input and returns true if
   * the separator should be shown after that input. Defaults to nothing.
   * For example, the following will put a separator in between every 3rd input:
   * ```jsx
   * <CodeInput shouldShowSeparator={(index) => (index + 1) % 3 === 0} />
   * ```
   */
  shouldShowSeparator?(index: number): boolean
}

export const CodeInput = React.forwardRef<HTMLInputElement, CodeInputProps>(
  function (props, ref) {
    const {
      label,
      name,
      length,
      separator,
      shouldShowSeparator,
      disabled,
      testId,
      invalid,
      errorText,
      helperText,
      preserveHelpSpace,
      required,
      containerClassName = '',
      size = 'auto',
      autoComplete = false
    } = props
    const preparedTestId = useUniqueId(testId, 'code-input-')
    const { getInputProps } = useCodeInput({ ...props, id: undefined }, ref)
    loadStrings()
    return (
      <div className={containerClassName}>
        {label && (
          <Label
            name={autoComplete ? `${name}-auto-complete` : name}
            disabled={disabled}
            required={required}
          >
            {label}
          </Label>
        )}

        <div
          className={cx(
            'flex flex-row items-center gap-1 xs:gap-3',
            disabled ? 'text-disabled' : 'text-default'
          )}
        >
          {Array.from({ length }, (_, i) => {
            const showSeparator =
              separator &&
              i < length - 1 &&
              (!shouldShowSeparator || shouldShowSeparator(i))
            return (
              <React.Fragment key={i}>
                <TextInput
                  {...getInputProps(i)}
                  testId={`${preparedTestId}-${i}`}
                  aria-label={t('aria-label.code-input', {
                    label: label,
                    number: i + 1
                  })}
                  size={size}
                  minLength={1}
                  maxLength={length}
                  inputMode='numeric'
                  className='text-center'
                  containerClassName='flex-grow'
                  invalid={invalid}
                  disabled={disabled}
                  required={required}
                  autoComplete={autoComplete ? 'one-time-code' : 'off'}
                />
                {showSeparator && (
                  <Separator
                    separator={separator}
                    testId={preparedTestId + '-separator'}
                  />
                )}
              </React.Fragment>
            )
          })}
        </div>
        <HelperText
          testId={preparedTestId + '-helper-text'}
          disabled={disabled}
          invalid={invalid}
          errorText={errorText}
          helperText={helperText}
          preserveHelpSpace={preserveHelpSpace}
        />
      </div>
    )
  }
)
